// Typed client for the Daily Focus rotating-subject set.
// Server: student-agent-api/routers/daily_focus_router.py mounted at
// /student/daily-focus. Mirrors the aptitude drill contract (and reuses its
// question/answer types) but the subject is chosen per student/day.

import api from './api';
import { DrillAnswerResponse, DrillProgress, DrillQuestion } from './aptitudeDrillAPI';

export interface FocusTodayResponse {
  session_id: string;
  focus_date: string;
  subject: string;
  status: 'in_progress' | 'completed' | 'abandoned';
  total_questions: number;
  questions: DrillQuestion[];
  progress: DrillProgress;
}

export interface FocusTopicBreakdown {
  answered: number;
  correct: number;
}

export interface FocusNextStep {
  kind: 'drill_topic' | 'strong' | 'tomorrow' | string;
  subject: string | null;
  topic?: string;
  label: string;
}

export interface FocusCompleteResponse {
  session_id: string;
  focus_date: string;
  subject: string;
  status: string;
  score: number;
  total: number;
  by_topic: Record<string, FocusTopicBreakdown>;
  weakest_today: { topic: string; correct: number; answered: number } | null;
  next_step: FocusNextStep;
}

// Read-only today's-focus state — never creates a session (used by the
// dashboard card's passive poll, so a dashboard load doesn't stamp a session).
export interface FocusStatusResponse {
  subject: string | null;
  status: 'not_started' | 'in_progress' | 'completed';
  answered: number;
  total: number;
}

// `subject` (optional) targets a specific subject's set (per-subject drill /
// picker); omitted → the daily rotation chooses.
export const dailyFocusAPI = {
  status: async (subject?: string): Promise<FocusStatusResponse> => {
    const response = await api.get<FocusStatusResponse>('/student/daily-focus/status', {
      params: subject ? { subject } : undefined,
    });
    return response.data;
  },

  today: async (subject?: string): Promise<FocusTodayResponse> => {
    const response = await api.get<FocusTodayResponse>('/student/daily-focus/today', {
      params: subject ? { subject } : undefined,
    });
    return response.data;
  },

  answer: async (
    question_id: string,
    selected_answer: string,
    time_spent_seconds: number,
    subject?: string,
  ): Promise<DrillAnswerResponse> => {
    const response = await api.post<DrillAnswerResponse>(
      '/student/daily-focus/answer',
      { question_id, selected_answer, time_spent_seconds },
      { params: subject ? { subject } : undefined },
    );
    return response.data;
  },

  complete: async (subject?: string): Promise<FocusCompleteResponse> => {
    const response = await api.post<FocusCompleteResponse>(
      '/student/daily-focus/complete',
      {},
      { params: subject ? { subject } : undefined },
    );
    return response.data;
  },

  // Pause daily-focus reminders for N days (0 resumes). Affects the bot DM
  // only — the web card stays available.
  snooze: async (days: number = 3): Promise<{ snooze_until: string | null; days: number }> => {
    const response = await api.post('/student/daily-focus/snooze', { days });
    return response.data;
  },
};
